import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import { GroupedClothesItem } from "../store/clothesStore";
import { Link } from "react-router-dom";

// Типы
interface ClothesTableProps {
  data: GroupedClothesItem[];
  isLoading?: boolean;
  onEdit: (item: any) => void;
  onDelete: (id: number) => void;
  onCopyLink: (id: number) => void;
  formatPrice: (price: string) => string;
  // Пагинация
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

// Стилизованные компоненты (копируем из InventoryTable)
const TableWrapper = styled.div`
  width: 100%;
  overflow: visible;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  flex: 1;
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
`;

const FilterRow = styled.div`
  display: none;
  user-select: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 2.5fr 1.5fr 1.2fr 1.5fr;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin-bottom: 8px;
    column-gap: 6px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.7fr 1.6fr 1.3fr 1.6fr;
    column-gap: 10px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.9fr 1.7fr 1.4fr 1.7fr;
    column-gap: 12px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 3fr 1.8fr 1.5fr 1.8fr;
    column-gap: 14px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 3.2fr 1.9fr 1.6fr 1.9fr;
    column-gap: 16px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3.4fr 2fr 1.7fr 2fr;
    column-gap: 18px;
    padding: 0 20px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.6fr 2.1fr 1.8fr 2.1fr;
    column-gap: 20px;
    padding: 0 24px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 4fr 2.3fr 2fr 2.3fr;
    column-gap: 24px;
    padding: 0 32px;
  }
`;

const FilterItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  width: 100%;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.$clickable ? "rgba(96, 159, 148, 0.08)" : "transparent"};
  }

  &:first-child {
    justify-content: flex-start;
  }
`;

const FilterLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609f94;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }
`;

const SortIcon = styled.div<{ direction: "asc" | "desc" | "none" }>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  flex-shrink: 0;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.direction === "none" ? 0.3 : 1)};

  ${(props) => {
    if (props.direction === "asc") {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    } else if (props.direction === "desc") {
      return `
        border-top: 6px solid #609F94;
        border-bottom: none;
      `;
    } else {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    }
  }}

  @media (min-width: 768px) {
    border-left-width: 5px;
    border-right-width: 5px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "7px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "7px" : "0")};
  }

  @media (min-width: 1024px) {
    border-left-width: 6px;
    border-right-width: 6px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "8px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "8px" : "0")};
  }
`;

const CardWithExpandWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
`;

const CardContainer = styled.div<{ $expanded?: boolean }>`
  background-color: #1a2e2e;
  border-radius: 12px;
  border: 1px solid #2c4a4a;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  position: relative;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
    border-color: #3d5a5a;
  }
`;


const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-height: 80px;
  column-gap: 8px;
  padding: 12px;
  overflow: visible;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 2.5fr 1.5fr 1.2fr 1.5fr;
    column-gap: 6px;
    padding: 12px;
    min-height: 60px;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.7fr 1.6fr 1.3fr 1.6fr;
    column-gap: 10px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.9fr 1.7fr 1.4fr 1.7fr;
    column-gap: 12px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 3fr 1.8fr 1.5fr 1.8fr;
    column-gap: 14px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 3.2fr 1.9fr 1.6fr 1.9fr;
    column-gap: 16px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3.4fr 2fr 1.7fr 2fr;
    column-gap: 18px;
    padding: 12px 20px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.6fr 2.1fr 1.8fr 2.1fr;
    column-gap: 20px;
    padding: 12px 24px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 4fr 2.3fr 2fr 2.3fr;
    column-gap: 24px;
    padding: 12px 32px;
  }
`;

const MobileProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileProductHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MobileProductInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #2c4a4a;
  gap: 8px;
`;

const MobileArticleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileProductPrice = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #24ba7d;
`;

const MobileProductArticle = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #8b9ba8;
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: transparent;
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 6px;
  }

  @media (min-width: 900px) {
    width: 52px;
    height: 52px;
    border-radius: 6px;
  }

  @media (min-width: 1024px) {
    width: 56px;
    height: 56px;
    border-radius: 7px;
  }

  @media (min-width: 1280px) {
    width: 64px;
    height: 64px;
    border-radius: 7px;
  }

  @media (min-width: 1440px) {
    width: 80px;
    height: 80px;
    border-radius: 8px;
  }

  @media (min-width: 1680px) {
    width: 96px;
    height: 96px;
    border-radius: 10px;
  }

  @media (min-width: 1920px) {
    width: 112px;
    height: 112px;
    border-radius: 12px;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const ProductName = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1920px) {
    font-size: 15px;
  }
`;

const ProductSubtitle = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 10px;
  color: #609F94;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 9px;
  }

  @media (min-width: 900px) {
    font-size: 10px;
  }

  @media (min-width: 1280px) {
    font-size: 11px;
  }

  @media (min-width: 1440px) {
    font-size: 12px;
  }

  @media (min-width: 1920px) {
    font-size: 13px;
  }
`;

const DesktopContent = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SizeLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;

  @media (min-width: 768px) {
    font-size: 11px;
    gap: 3px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
    gap: 4px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
    gap: 5px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
    gap: 6px;
  }
`;

const SizesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
`;

const SizeChip = styled.span`
  background-color: #0e1f1f;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 10px;
    padding: 3px 6px;
  }

  @media (min-width: 900px) {
    font-size: 10px;
    padding: 4px 7px;
  }

  @media (min-width: 1280px) {
    font-size: 11px;
    padding: 5px 8px;
  }

  @media (min-width: 1440px) {
    font-size: 12px;
    padding: 6px 10px;
  }

  @media (min-width: 1920px) {
    font-size: 14px;
    padding: 8px 12px;
  }
`;

const SizeCount = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #609f94;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const ArticleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ArticleLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #8b9ba8;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1024px) {
    font-size: 12px;
  }
`;

const CopyArticleButton = styled.button`
  background: #1a2e2e;
  border: 1px solid #2c4a4a;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding: 0;
  color: #8b9ba8;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(96, 159, 148, 0.15);
    border-color: #609f94;
    color: #609f94;
  }

  &:active {
    background-color: rgba(96, 159, 148, 0.25);
    transform: scale(0.95);
  }

  svg {
    width: 13px;
    height: 13px;
    opacity: 0.8;
  }

  @media (min-width: 1024px) {
    width: 32px;
    height: 32px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const ExpandableContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${props => props.$expanded ? 'auto' : '0'};
  opacity: ${props => props.$expanded ? '1' : '0'};
  transition: max-height 0.3s ease, opacity 0.3s ease;
  background-color: #132525;

  @media (max-width: 767px) {
    max-height: ${props => props.$expanded ? '8000px' : '0'};
  }
`;

const SizeVariantContainer = styled.div`
  padding: 16px;
  border-top: 1px solid #2c4a4a;

  @media (min-width: 768px) {
    padding: 16px 20px;
  }

  @media (min-width: 1024px) {
    padding: 20px;
  }
`;

const SizeVariantContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SizeVariantInfo = styled.div`
  flex: 1;
`;

const SizeVariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const SizeVariantActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    margin-top: 12px;
    border-top: 1px solid #2c4a4a;
    padding-top: 12px;
  }

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const SizeVariantPrice = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #24BA7D;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 20px;
  }

  @media (min-width: 1440px) {
    font-size: 20px;
  }

  @media (min-width: 1920px) {
    font-size: 20px;
  }
`;

const StatusButton = styled.button<{ $color: string }>`
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.$color};
  background-color: transparent;
  color: ${(props) => props.$color};
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => props.$color};
    color: #0e1f1f;
    transform: translateY(-1px);
  }
`;

const IconButtonsColumn = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  justify-content: center;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 6px;
  }
`;


const InfoPill = styled.div`
  background-color: #0E1F1F;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    padding: 10px 14px;
  }
`;

const InfoLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-size: 10px;
  color: #609F94;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 1024px) {
    font-size: 12px;
  }
`;

const InfoValue = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }
`;


const IconButton = styled.button`
  background: #1a2e2e;
  border: 1px solid #2c4a4a;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(96, 159, 148, 0.15);
    border-color: #609f94;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    background-color: rgba(96, 159, 148, 0.2);
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.85;
  }

  @media (min-width: 768px) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ProductLink = styled(Link)`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #609f94;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: color 0.2s ease;

  &:hover {
    color: #24ba7d;
  }

  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

// Пагинация (копируем из InventoryTable)
const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0 12px;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    padding: 0 16px;
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.16)' : 'rgba(8, 28, 33, 0.48)'};
  border: 1px solid ${props => props.$active ? '#24BA7D' : 'rgba(255, 255, 255, 0.16)'};
  color: ${props => props.$active ? '#24BA7D' : '#FFFFFF'};
  font-family: 'Ubuntu', sans-serif;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.24)' : 'rgba(8, 28, 33, 0.64)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  margin-right: 0;
  text-align: center;
  flex: 1;
  min-width: 200px;
  
  @media (min-width: 768px) {
    font-size: 14px;
    margin-right: auto;
    flex: none;
    min-width: auto;
  }
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0;
  
  @media (min-width: 768px) {
    margin-left: 16px;
  }
`;

const ItemsPerPageLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin-right: 8px;
`;

const ItemsPerPageSelect = styled.select`
  background-color: rgba(8, 28, 33, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(36, 186, 125, 0.48);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background-color: #172A2A;
  }
`;

// Вспомогательная функция для группировки размеров
const groupSizesByValue = (sizes: any[]) => {
  const grouped: { [key: string]: number } = {};
  sizes.forEach((size) => {
    const sizeValue = size.size;
    grouped[sizeValue] = (grouped[sizeValue] || 0) + 1;
  });
  return Object.entries(grouped).map(([size, count]) => ({ size, count }));
};

const ClothesTable: React.FC<ClothesTableProps> = ({
  data,
  isLoading,
  onEdit,
  onDelete,
  onCopyLink,
  formatPrice,
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  // Определяем колонки для таблицы
  const columns = useMemo<ColumnDef<GroupedClothesItem>[]>(
    () => [
      {
        id: "brand",
        accessorKey: "brand",
        header: "Бренд",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <>
              <MobileProductCard>
                <MobileProductHeader>
                  <ProductImage
                    style={{ backgroundImage: `url(${item.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{item.brand}</ProductName>
                    <ProductSubtitle>{item.model}</ProductSubtitle>
                  </ProductDetails>
                </MobileProductHeader>
                <SizesContainer>
                  {groupSizesByValue(item.items)
                    .slice(0, 8)
                    .map((groupedSize, index) => (
                      <SizeChip key={index}>
                        {groupedSize.size}
                        {groupedSize.count > 1 && (
                          <span style={{ color: "#24BA7D" }}>
                            {" "}
                            x{groupedSize.count}
                          </span>
                        )}
                      </SizeChip>
                    ))}
                  {groupSizesByValue(item.items).length > 8 && (
                    <SizeCount style={{ marginLeft: "4px" }}>
                      +{groupSizesByValue(item.items).length - 8}
                    </SizeCount>
                  )}
                </SizesContainer>
                <MobileProductInfo>
                  <MobileProductPrice>
                    {formatPrice(
                      item.priceRange.min === item.priceRange.max
                        ? `${item.priceRange.min} Р`
                        : `${item.priceRange.min} - ${item.priceRange.max} Р`
                    )}
                  </MobileProductPrice>
                  <MobileArticleWrapper>
                    <MobileProductArticle>{item.article}</MobileProductArticle>
                    <CopyArticleButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(item.article);
                      }}
                      title="Копировать артикул"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.66667 2.5H12.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.83333 4.16667C5.83333 3.25 6.58333 2.5 7.5 2.5H8.81667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.5 6.66667V11.8083C17.5 12.7167 16.7583 13.4667 15.85 13.4667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.5 6.66667H15C12.8333 6.66667 12.5 6.33333 12.5 4.16667V1.66667L17.5 6.66667Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </CopyArticleButton>
                  </MobileArticleWrapper>
                </MobileProductInfo>
              </MobileProductCard>
              <DesktopContent>
                <ProductInfo>
                  <ProductImage
                    style={{ backgroundImage: `url(${item.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{item.brand}</ProductName>
                    <ProductSubtitle>{item.model}</ProductSubtitle>
                  </ProductDetails>
                </ProductInfo>
              </DesktopContent>
            </>
          );
        },
      },
      {
        id: "sizes",
        header: "Размер",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DesktopContent>
              <SizeLabel>
                <SizesContainer>
                  {groupSizesByValue(item.items)
                    .slice(0, 12)
                    .map((groupedSize, index) => (
                      <SizeChip key={index}>
                        {groupedSize.size}
                        {groupedSize.count > 1 && (
                          <span style={{ color: "#24BA7D" }}>
                            {" "}
                            x{groupedSize.count}
                          </span>
                        )}
                      </SizeChip>
                    ))}
                  {groupSizesByValue(item.items).length > 12 && (
                    <SizeCount style={{ marginLeft: "4px" }}>
                      +{groupSizesByValue(item.items).length - 12}
                    </SizeCount>
                  )}
                </SizesContainer>
              </SizeLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const aSizes = rowA.original.items.map((i) =>
            parseFloat(i.size.replace(/[^\d.]/g, ""))
          );
          const bSizes = rowB.original.items.map((i) =>
            parseFloat(i.size.replace(/[^\d.]/g, ""))
          );
          const aSize = aSizes.length > 0 ? Math.min(...aSizes) : 0;
          const bSize = bSizes.length > 0 ? Math.min(...bSizes) : 0;
          return aSize - bSize;
        },
      },
      {
        id: "price",
        accessorKey: "priceRange",
        header: "Цена",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DesktopContent>
              <div>
                {formatPrice(
                  item.priceRange.min === item.priceRange.max
                    ? `${item.priceRange.min} Р`
                    : `${item.priceRange.min} - ${item.priceRange.max} Р`
                )}
              </div>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const avgPriceA = rowA.original.priceRange.min;
          const avgPriceB = rowB.original.priceRange.min;
          return avgPriceA - avgPriceB;
        },
      },
      {
        id: "article",
        accessorKey: "article",
        header: "Артикул",
        cell: ({ row }) => {
          const item = row.original;
          
          const handleCopyArticle = (e: React.MouseEvent) => {
            e.stopPropagation();
            navigator.clipboard.writeText(item.article);
          };

          return (
            <DesktopContent>
              <ArticleWrapper>
                <ArticleLabel>{item.article}</ArticleLabel>
                <CopyArticleButton
                  onClick={handleCopyArticle}
                  title="Копировать артикул"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.66667 2.5H12.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.83333 4.16667C5.83333 3.25 6.58333 2.5 7.5 2.5H8.81667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.5 6.66667V11.8083C17.5 12.7167 16.7583 13.4667 15.85 13.4667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.5 6.66667H15C12.8333 6.66667 12.5 6.33333 12.5 4.16667V1.66667L17.5 6.66667Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </CopyArticleButton>
              </ArticleWrapper>
            </DesktopContent>
          );
        },
      },
    ],
    [formatPrice]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const toggleRowExpansion = (article: string) => {
    setExpanded((prev) => ({
      ...prev,
      [article]: !prev[article],
    }));
  };

  if (isLoading && data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
          color: "#FFFFFF",
        }}
      >
        Загрузка...
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div style={{ color: "#FFFFFF", textAlign: "center", padding: "24px 0" }}>
        Инвентарь пуст. Добавьте товары, нажав кнопку "Добавить".
      </div>
    );
  }

  return (
    <TableWrapper>
      <CardsContainer>
        <FilterRow>
          {table.getHeaderGroups()[0].headers.map((header) => (
            <FilterItem
              key={header.id}
              $clickable={header.column.getCanSort()}
              onClick={header.column.getToggleSortingHandler()}
            >
              <FilterLabel>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </FilterLabel>
              {header.column.getCanSort() && (
                <SortIcon
                  direction={
                    header.column.getIsSorted() === "asc"
                      ? "asc"
                      : header.column.getIsSorted() === "desc"
                      ? "desc"
                      : "none"
                  }
                />
              )}
            </FilterItem>
          ))}
        </FilterRow>

        {table.getRowModel().rows.map((row) => {
          const item = row.original;
          const isExpanded = expanded[item.article];

          return (
            <CardWithExpandWrapper key={item.article}>
              <CardContainer 
                $expanded={isExpanded}
                onClick={() => toggleRowExpansion(item.article)}
              >
                <CardRow>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </React.Fragment>
                  ))}
                </CardRow>

                <ExpandableContent $expanded={isExpanded}>
                  {item.items.map((clothesItem) => (
                    <SizeVariantContainer
                      key={clothesItem.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SizeVariantContent>
                        <SizeVariantInfo>
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              backgroundColor: '#0E1F1F',
                              border: '1px solid #2C4A4A',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#FFFFFF',
                              display: 'inline-block',
                              minWidth: '80px',
                              textAlign: 'center'
                            }}>
                              {clothesItem.size}
                            </div>
                          </div>
                          <SizeVariantGrid>
                            <InfoPill>
                              <InfoLabel>Состояние</InfoLabel>
                              <InfoValue>{clothesItem.condition}</InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Город</InfoLabel>
                              <InfoValue>{clothesItem.city}</InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Место</InfoLabel>
                              <InfoValue>{clothesItem.place}</InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Примерка</InfoLabel>
                              <InfoValue>{clothesItem.fitting ? 'Да' : 'Нет'}</InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Подробнее</InfoLabel>
                              <ProductLink
                                to={`/clothes/${clothesItem.id}`}
                                state={{ from: "inventory" }}
                                style={{ fontSize: '12px', padding: '2px 0' }}
                              >
                                Открыть →
                              </ProductLink>
                            </InfoPill>
                          </SizeVariantGrid>
                        </SizeVariantInfo>

                        <SizeVariantActions>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                          }}>
                            <SizeVariantPrice>
                              {formatPrice(clothesItem.price)}
                            </SizeVariantPrice>
                            <IconButtonsColumn>
                              <IconButton
                                onClick={() => onCopyLink(clothesItem.id)}
                                title="Копировать ссылку"
                              >
                                <CopyIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => onEdit(clothesItem)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => onDelete(clothesItem.id)}>
                                <TrashIcon />
                              </IconButton>
                            </IconButtonsColumn>
                          </div>
                        </SizeVariantActions>
                      </SizeVariantContent>
                    </SizeVariantContainer>
                  ))}
                </ExpandableContent>
              </CardContainer>
            </CardWithExpandWrapper>
          );
        })}
      </CardsContainer>

      {/* Pagination */}
      {onPageChange && totalItems > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            {data.length === 0
              ? 'Нет элементов'
              : `Показано ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} из ${totalItems}`
            }
          </PaginationInfo>
          {renderPaginationButtons(currentPage, Math.ceil(totalItems / itemsPerPage), onPageChange, isLoading)}
          {onItemsPerPageChange && (
            <ItemsPerPageContainer>
              <ItemsPerPageLabel>Показывать по:</ItemsPerPageLabel>
              <ItemsPerPageSelect
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                disabled={isLoading}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                {totalItems > 0 && <option value={totalItems}>Все</option>}
              </ItemsPerPageSelect>
            </ItemsPerPageContainer>
          )}
        </PaginationContainer>
      )}
    </TableWrapper>
  );
};

// Функция рендеринга кнопок пагинации
const renderPaginationButtons = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
  isLoading?: boolean
) => {
  const buttons = [];
  const maxVisiblePages = 5;

  if (currentPage > 1) {
    buttons.push(
      <PageButton
        key="first"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1 || isLoading}
      >
        <ArrowLeftDoubleIcon />
      </PageButton>
    );
  }

  if (currentPage > 1) {
    buttons.push(
      <PageButton
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
      >
        <ArrowLeftIcon />
      </PageButton>
    );
  }

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <PageButton
        key={i}
        $active={i === currentPage}
        onClick={() => onPageChange(i)}
        disabled={isLoading}
      >
        {i}
      </PageButton>
    );
  }

  if (currentPage < totalPages) {
    buttons.push(
      <PageButton
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
      >
        <ArrowRightIcon />
      </PageButton>
    );
  }

  if (currentPage < totalPages) {
    buttons.push(
      <PageButton
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || isLoading}
      >
        <ArrowRightDoubleIcon />
      </PageButton>
    );
  }

  return buttons;
};

// Иконки пагинации
const ArrowLeftDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.2 12.4333L3.96667 9.2C3.43334 8.66667 3.43334 7.78 3.96667 7.24667L7.2 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.0333 12.4333L8.79999 9.2C8.26666 8.66667 8.26666 7.78 8.79999 7.24667L12.0333 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.96667 12.4333L12.2 9.2C12.7333 8.66667 12.7333 7.78 12.2 7.24667L8.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.96667 12.4333L7.20001 9.2C7.73334 8.66667 7.73334 7.78 7.20001 7.24667L3.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0334 4.01334L6.80002 7.24667C6.26669 7.78 6.26669 8.66667 6.80002 9.2L10.0334 12.4333" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.96667 12.4333L9.20001 9.2C9.73334 8.66667 9.73334 7.78 9.20001 7.24667L5.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Иконки
const CopyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.66667 2.5H12.5"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.83333 4.16667C5.83333 3.25 6.58333 2.5 7.5 2.5H8.81667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 6.66667V11.8083C17.5 12.7167 16.7583 13.4667 15.85 13.4667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 6.66667H15C12.8333 6.66667 12.5 6.33333 12.5 4.16667V1.66667L17.5 6.66667Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.16667 1.66667H7.5C3.33333 1.66667 1.66667 3.33334 1.66667 7.5V12.5C1.66667 16.6667 3.33333 18.3333 7.5 18.3333H12.5C16.6667 18.3333 18.3333 16.6667 18.3333 12.5V10.8333"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3667 2.51665L6.8 9.08332C6.55 9.33332 6.3 9.82499 6.25 10.1833L5.89167 12.6917C5.75833 13.6 6.4 14.2333 7.30833 14.1083L9.81667 13.75C10.1667 13.7 10.6583 13.45 10.9167 13.2L17.4833 6.63332C18.6167 5.49999 19.15 4.18332 17.4833 2.51665C15.8167 0.849985 14.5 1.38332 13.3667 2.51665Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.425 3.45834C12.9833 5.45 14.5417 7.00834 16.5417 7.575"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 4.98333C14.725 4.70833 11.9333 4.56667 9.15 4.56667C7.5 4.56667 5.85 4.65 4.2 4.81667L2.5 4.98333"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.08333 4.14167L7.26666 3.05C7.39999 2.25833 7.5 1.66667 8.90833 1.66667H11.0917C12.5 1.66667 12.6083 2.29167 12.7333 3.05833L12.9167 4.14167"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.7084 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32502C5.00002 18.3333 4.92502 17.3167 4.83336 16.0083L4.29169 7.61667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.60834 13.75H11.3833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.91666 10.4167H12.0833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default ClothesTable;
